import {
  Archive,
  ArchiveRestore,
  CalendarPlus,
  ClipboardPlus,
  Download,
  EllipsisVertical,
  FileText,
  HeartPulse,
  Loader2,
  Pencil,
  PenSquare,
  Pill,
  Smile,
  Stethoscope,
} from 'lucide-react'
import { useState } from 'react'
import { useParams } from 'react-router-dom'

import type { GetPatientResponse } from '@snootsvet/shared'

import { DrawerOverlay } from '@/frontend/components/drawer-overlay'
import { FormattedDate } from '@/frontend/components/formatted-datetime'
import { Alert, AlertDescription, AlertTitle } from '@/frontend/components/ui/alert'
import { Button } from '@/frontend/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/frontend/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/frontend/components/ui/dropdown-menu'
import { BookAppointmentDialog } from '@/frontend/features/appointments/book-appointment-dialog'
import {
  ActiveConsultationProvider,
  useActiveConsultation,
} from '@/frontend/features/consultations/active-consultation-context'
import { CreateConsultationDialog } from '@/frontend/features/consultations/create-consultation-action'
import { QuickPrescribePanel } from '@/frontend/features/consultations/quick-prescribe-panel'
import { useDownloadPatientHistory } from '@/frontend/features/documents/use-download-patient-history'
import { NoteForm } from '@/frontend/features/notes/note.form'
import { AccountStatusCard } from '@/frontend/features/orders/account-status-card'
import { OutstandingOrdersCard } from '@/frontend/features/orders/outstanding-orders-card'
import { ArchivePatientDialog } from '@/frontend/features/patient/archive-patient-action'
import { DentalRequestDialog } from '@/frontend/features/patient/dental-request-dialog'
import { OwnerCard } from '@/frontend/features/patient/owner-card'
import { PatientAvatar } from '@/frontend/features/patient/patient-avatar'
import { PatientDetailsCard } from '@/frontend/features/patient/patient-details-card'
import { PatientFilesCard } from '@/frontend/features/patient/patient-files-card'
import { PatientPhotoDialog } from '@/frontend/features/patient/patient-photo-dialog'
import { PatientSummarySection } from '@/frontend/features/patient/patient-summary-section'
import { PatientTimeline } from '@/frontend/features/patient/patient-timeline'
import { PrescriptionsCard } from '@/frontend/features/patient/prescriptions-card'
import { RequestProcedureDialog } from '@/frontend/features/patient/request-procedure-dialog'
import { VaccinationsCard } from '@/frontend/features/patient/vaccinations-card'
import { VitalsFormDialog } from '@/frontend/features/patient/vitals-form-dialog'
import { GenerateDocumentDialog } from '@/frontend/features/printing/documents/generate-document-action'
import { NewTaskDialog } from '@/frontend/features/tasks/new-task-dialog'
import { PatientTasksCard } from '@/frontend/features/tasks/patient-tasks-card'
import { useGetPatient } from '@/frontend/hooks/api/patients/use-get-patient'
import { useBreadcrumbs } from '@/frontend/hooks/use-breadcrumbs'
import { useDocumentTitle } from '@/frontend/hooks/use-document-title'
import { routes } from '@/frontend/routes'

/** Patient page — shows header, timeline, and sidebar summary cards. */
export function PatientPage() {
  const { patientId } = useParams<{ patientId: string }>()
  const { data, isLoading, error } = useGetPatient(patientId!)
  const patient = data?.patient

  useBreadcrumbs(
    patient
      ? [{ label: 'Patients', to: routes.patients }, { label: patient?.name ?? 'Patient' }]
      : undefined
  )

  useDocumentTitle(patient?.name)

  if (isLoading) return <div>Loading...</div>
  if (error || !patient) return <div>Error loading patient.</div>

  return (
    <ActiveConsultationProvider patientId={patient.id}>
      <PatientPageInner patient={patient} />
    </ActiveConsultationProvider>
  )
}

function PatientPageInner({ patient }: { patient: GetPatientResponse['patient'] }) {
  const { consultationId } = useActiveConsultation()
  const [prescribeOpen, setPrescribeOpen] = useState(false)
  const [appointmentOpen, setAppointmentOpen] = useState(false)
  const [taskOpen, setTaskOpen] = useState(false)
  const [noteOpen, setNoteOpen] = useState(false)
  const [procedureOpen, setProcedureOpen] = useState(false)
  const [vitalsOpen, setVitalsOpen] = useState(false)
  const [photoOpen, setPhotoOpen] = useState(false)

  return (
    <div>
      {patient.archivedAt && (
        <Alert variant="warning" className="mb-3">
          <Archive />
          <AlertTitle>
            {/* Archiving is an administrative action with no clinic in scope. */}
            Archived on <FormattedDate value={patient.archivedAt} clinicId={null} />
          </AlertTitle>
          {patient.archivedReason && <AlertDescription>{patient.archivedReason}</AlertDescription>}
        </Alert>
      )}

      <div className="mb-3 flex flex-col gap-3">
        {/* Photo + name + actions */}
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex max-w-full shrink-0 items-center justify-center gap-3 lg:justify-start">
            <button
              type="button"
              aria-label="Edit patient photo"
              onClick={() => setPhotoOpen(true)}
              className="group relative shrink-0 cursor-pointer rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <PatientAvatar src={patient.photoUrl} species={patient.species} name={patient.name} />
              <span className="absolute inset-0 flex items-center justify-center rounded-full bg-black/40 text-white opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
                <Pencil className="size-5" />
              </span>
            </button>
            <h1 className="min-w-0 break-words text-headline-lg">{patient.name}</h1>
          </div>
          <div className="flex min-w-0 flex-wrap items-center justify-center gap-2 lg:justify-end">
            <Button variant="outline" size="lg" onClick={() => setNoteOpen(true)}>
              <PenSquare className="size-4" />
              Add Note/File
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={() => setPrescribeOpen(true)}
              disabled={consultationId !== null}
              title={consultationId ? 'Close the active consultation first' : undefined}
            >
              <Pill className="size-4" />
              Prescribe
            </Button>
            <Button variant="outline" size="lg" onClick={() => setAppointmentOpen(true)}>
              <CalendarPlus className="size-4" />
              Book Appointment
            </Button>
            <Button variant="outline" size="lg" onClick={() => setTaskOpen(true)}>
              <ClipboardPlus className="size-4" />
              Add Task
            </Button>
            <Button variant="outline" size="lg" onClick={() => setProcedureOpen(true)}>
              <Stethoscope className="size-4" />
              Request Procedure
            </Button>
            <Button variant="outline" size="lg" onClick={() => setVitalsOpen(true)}>
              <HeartPulse className="size-4" />
              Record Vitals
            </Button>
            <PatientKebabMenu patient={patient} />
          </div>
        </div>

        <PatientSummarySection key={patient.id} patientId={patient.id} />
      </div>

      {/* Two-column layout: timeline + sidebar */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_330px] 2xl:grid-cols-[minmax(0,1fr)_500px]">
        {/* Main column — Timeline */}
        <div className="order-2 lg:order-1">
          <PatientTimeline patient={patient} />
        </div>

        {/* Summary cards — right rail on desktop, stacked above the timeline on smaller screens */}
        <aside className="order-1 space-y-4 lg:order-2">
          <PatientDetailsCard key={patient.id} patient={patient} />
          <AccountStatusCard
            patientId={patient.id}
            memberId={patient.owner.id}
            memberEmail={patient.owner.email}
            memberRegion={patient.owner.region}
          />
          <OwnerCard owner={patient.owner} />
          <OutstandingOrdersCard patientId={patient.id} />
          <PatientTasksCard patientId={patient.id} />
          <PrescriptionsCard patientId={patient.id} />
          <VaccinationsCard patientId={patient.id} />
          <PatientFilesCard patientId={patient.id} />
        </aside>
      </div>

      <VitalsFormDialog patientId={patient.id} open={vitalsOpen} onOpenChange={setVitalsOpen} />

      <RequestProcedureDialog
        patient={patient}
        open={procedureOpen}
        onOpenChange={setProcedureOpen}
      />

      {photoOpen && (
        <PatientPhotoDialog
          key={patient.id}
          patient={patient}
          onClose={() => setPhotoOpen(false)}
        />
      )}

      <NewTaskDialog
        open={taskOpen}
        onOpenChange={setTaskOpen}
        defaultPatientId={patient.id}
        subtitle={patient.name}
      />

      {/* Add/Edit note dialog */}
      {/* Outside-click dismissal is disabled to avoid losing entered notes. */}
      <Dialog open={noteOpen} onOpenChange={setNoteOpen} disablePointerDismissal>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add Note — {patient.name}</DialogTitle>
          </DialogHeader>
          <NoteForm
            patientId={patient.id}
            onSuccess={() => setNoteOpen(false)}
            onCancel={() => setNoteOpen(false)}
          />
        </DialogContent>
      </Dialog>

      <BookAppointmentDialog
        open={appointmentOpen}
        onOpenChange={setAppointmentOpen}
        defaultPatientId={patient.id}
        subtitle={patient.name}
      />

      {/* Outside-click dismissal is disabled to avoid losing in-progress prescriptions. */}
      <Dialog open={prescribeOpen} onOpenChange={setPrescribeOpen} disablePointerDismissal>
        <DialogContent className="sm:max-w-2xl">
          <DrawerOverlay>
            <DialogHeader>
              <DialogTitle>Prescribe — {patient.name}</DialogTitle>
            </DialogHeader>
            <QuickPrescribePanel
              patientId={patient.id}
              onSuccess={() => setPrescribeOpen(false)}
              onCancel={() => setPrescribeOpen(false)}
            />
          </DrawerOverlay>
        </DialogContent>
      </Dialog>
    </div>
  )
}

/** Kebab menu for secondary patient actions — generate document, export, create consultation, dental request, archive. */
function PatientKebabMenu({ patient }: { patient: GetPatientResponse['patient'] }) {
  const [consultationOpen, setConsultationOpen] = useState(false)
  const [generateDocOpen, setGenerateDocOpen] = useState(false)
  const [dentalOpen, setDentalOpen] = useState(false)
  const [archiveOpen, setArchiveOpen] = useState(false)
  const { download: downloadHistory, isDownloading: isDownloadingHistory } =
    useDownloadPatientHistory(patient.id)

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger render={<Button variant="outline" size="icon" />}>
          <EllipsisVertical className="size-4" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" side="bottom">
          <DropdownMenuItem onClick={() => setConsultationOpen(true)}>
            <Stethoscope className="size-3.5" />
            Create Consultation
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setGenerateDocOpen(true)}>
            <FileText className="size-3.5" />
            Generate Document
          </DropdownMenuItem>
          <DropdownMenuItem onClick={downloadHistory} disabled={isDownloadingHistory}>
            {isDownloadingHistory ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <Download className="size-3.5" />
            )}
            Download Patient History
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => setDentalOpen(true)}
            disabled={patient.dentalRequestedAt !== null}
          >
            <Smile className="size-3.5" />
            Request Dental Communication
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem variant="destructive" onClick={() => setArchiveOpen(true)}>
            {patient.archivedAt ? (
              <ArchiveRestore className="size-3.5" />
            ) : (
              <Archive className="size-3.5" />
            )}
            {patient.archivedAt ? 'Unarchive' : 'Archive'}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <CreateConsultationDialog
        patientId={patient.id}
        open={consultationOpen}
        onOpenChange={setConsultationOpen}
      />
      <GenerateDocumentDialog
        patient={patient}
        open={generateDocOpen}
        onOpenChange={setGenerateDocOpen}
      />
      <DentalRequestDialog patient={patient} open={dentalOpen} onOpenChange={setDentalOpen} />
      <ArchivePatientDialog
        patientId={patient.id}
        archivedAt={patient.archivedAt}
        ownerArchivedAt={patient.owner.archivedAt}
        open={archiveOpen}
        onOpenChange={setArchiveOpen}
      />
    </>
  )
}
